/**
 * Concert/post object that may have a ticket pricing schedule from the API.
 * Schedule can be at top level (ticket_pricing_schedule) or inside content_hash.
 */
export type ConcertWithPricingSchedule = {
  ticket_pricing_schedule?: TicketPricingScheduleEntry[]
  content_hash?: { ticket_pricing_schedule?: TicketPricingScheduleEntry[] }
}

export type TicketPricingScheduleEntry = {
  price_changes_at?: string
  price_changes_to?: number | string
}

export type NextFuturePriceChange = {
  price_changes_to: number
  price_changes_at: string
}

/**
 * Returns the next future price change from the concert's ticket_pricing_schedule,
 * or null if none. Checks both top-level ticket_pricing_schedule (from API) and
 * content_hash.ticket_pricing_schedule.
 */
export function getNextFuturePriceChange(
  concert: ConcertWithPricingSchedule | null | undefined,
): NextFuturePriceChange | null {
  const schedule =
    concert?.ticket_pricing_schedule ??
    concert?.content_hash?.ticket_pricing_schedule

  if (!schedule || !Array.isArray(schedule)) return null

  const now = new Date().getTime()
  const future: NextFuturePriceChange[] = []

  for (const e of schedule) {
    const atStr = e?.price_changes_at
    if (atStr == null || atStr === "") continue
    const at = new Date(atStr).getTime()
    if (Number.isNaN(at) || at <= now) continue
    const price = Number(e.price_changes_to)
    if (Number.isNaN(price)) continue
    future.push({
      price_changes_to: price,
      price_changes_at: String(atStr),
    })
  }

  if (future.length === 0) return null
  future.sort(
    (a, b) =>
      new Date(a.price_changes_at).getTime() -
      new Date(b.price_changes_at).getTime(),
  )
  return future[0]
}
